import DownloadIcon from '@mui/icons-material/Download';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import classNames from 'classnames';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { getLayerXYZData } from '../api/data';
import { Bounds, DataSourceModel, MapLocation } from '../core/global';
import { ErrorMessage } from './error-message';
import { ModalTitle } from './modal-title';
import HorizontalStepper from './stepper';

import { useState } from "react";

import GetAppIcon from '@mui/icons-material/GetApp';
import "react-leaflet-fullscreen/styles.css";


import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { useMap } from 'react-leaflet';
import { getTempToken } from '../api/auth';
import { AccountContext } from '../context/account';
import { MapDataContext } from '../context/map-data';
import { LayerDescription, ModelShape } from '../data/layers';
import { downloadDataAsCSV } from '../helpers/common';
import { buildUrl } from '../helpers/core';
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/export-xyz.module.scss";
import Loader from './loader';
import { MapPreview } from './map-preview';
import { ModelSelect } from './models-switch';
import { AreaBanner, BoundsBanner, getStrToCopy } from './mouse-coordinates';

export const API_BASE = import.meta.env.VITE_API_BASE || '';
const allowExportAllArea = false

export const ExportButton = () => {
  const [showImagePreview, setShowImagePreview] = useState<boolean>(false);
  
  const {
    zoom,
    dataSourceModel,
    dataSources,
  } = useContext(MapDataContext);

  const enableForThisDataSources = useMemo(() => {
    if (dataSourceModel && dataSourceModel?.key.indexOf('ondemand') === -1) {
      return true
    }
    return false
  }, [dataSourceModel])

  const canExportThisScale = useMemo(() => {
    if (!enableForThisDataSources) {
      return false
    }
    const settingsZoom  = import.meta.env.VITE_MIN_CUSTOM_ZOOM ? Number(import.meta.env.VITE_MIN_CUSTOM_ZOOM) : 8
    return !!(zoom >= settingsZoom)
  }, [zoom, enableForThisDataSources])

  
  return <>
    <Button
      onClick={(e: React.SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setShowImagePreview(true)
      }}
      disabled={!canExportThisScale}
      variant="contained"
      className={styles.exportButton}
    >
      <GetAppIcon />
    </Button>
    {showImagePreview && <ExportModal onClose={() => {
      setShowImagePreview(false)
    }} />}

  </>
}

const steps = ['Setup', 'Export']

interface Option {
  key: string
  title: string
}

const LayerSelector = ({
  id,
  value,
  onChange,
  options,
}: {
  id: string
  value: string
  options: Option[]
  onChange: (selectedValue: string, selectedItem: Option) => void
}) => {
  const title = `Data layer`
  return (
    <div>
      <FormControl required sx={{width: '100%'}}>
        <InputLabel id={id}>{title}</InputLabel>
        <Select
          label={title}
          // size="small"
          // style={{ width: "180px", marginRight: "8px" }}
          labelId={id}
          id={id}
          value={value}
          sx={{width: '100%'}}
        >
          {options.map((item, index) => {

            const {title, key} = item

            return (
              <MenuItem
                key={`${index}-${key}`}
                value={`${key}`}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  onChange(`${key}` as string, item)
                }}
              >
                {title}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  )
}

interface EditProjectModalProps {
  onClose: () => void
}

export const ExportModal = ({
  onClose,
}: EditProjectModalProps ) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [exportError, setExportError] = React.useState<string[]>([])
  const [currentMapCenterPreview, setCurrentMapCenterPreview] = React.useState<MapLocation | null>(null)
  const [exportModelArea, setExportModelArea] = React.useState<boolean>(false)
  const [exportModel, setExportModel] = useState<DataSourceModel | null>()
  const [selectedLayer, setSelectedLayer] = React.useState<string>('')
  const [loading, setLoading] = React.useState<boolean>(false)
  const [columnWidth, setColumnWidth] = React.useState<number>(0);

  const { 
    dataSources,
    dataSourceModel,
    currentLayer,
    currentMapCenter,
    setCurrentMapCenter,
    zoom,
  } = useContext(MapDataContext);
  
  const {
    account,
    setUserMessages,
  } = useContext(AccountContext);

  const map = useMap ();

  const columnRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (columnRef && columnRef.current) {
      const width = columnRef.current.clientWidth
      setColumnWidth(width)
    }
  }, [columnRef?.current])

  useEffect(() => {
    setExportModel(dataSourceModel)
    if (currentLayer) {
      const {valueKey} = currentLayer
      if (valueKey) {
        setSelectedLayer(valueKey)
      }
    }
    if (map) {
      const bounds = map.getBounds()
      if (bounds) {
        const locBounds = {
          min: {
            lat: bounds.getSouth(),
            lng: bounds.getWest(),
          },
          max: {
            lat: bounds.getNorth(),
            lng: bounds.getEast(),
          }
        }
        if (currentMapCenter) {
          setCurrentMapCenterPreview({
            ...currentMapCenter,
            bounds: locBounds
          })
        }
      }
    }
  }, [])

  useEffect(() => {
    setExportError([])
  }, [activeStep])
  
  const {bounds} =  useMemo(() => {
    let bounds = null
    if (currentMapCenterPreview) {
      bounds = currentMapCenterPreview.bounds
    }
    if (exportModelArea && exportModel) {
      const {key} = exportModel
      const modelDataSource = dataSources ? dataSources[key] : null
      if (modelDataSource) {
        const {max_lat, max_lon, min_lat, min_lon} = modelDataSource
        if (max_lat !== null && max_lon !== null && min_lat !== null && min_lon !== null) {
          bounds = {
            min: {
              lat: Number(min_lat),
              lng: Number(min_lon),
            },
            max: {
              lat: Number(max_lat),
              lng: Number(max_lon),
            }
          }
        }
      }
    }
    return {bounds}
  }, [currentMapCenterPreview, exportModelArea, exportModel])

  const layersSelector: Option[] = useMemo(() => {
    if (dataSources && exportModel) {
      const modelDataSource = dataSources[exportModel.key]
      if (modelDataSource) {
        const {layers} = modelDataSource

        if (layers && Array.isArray(layers)) {
          const l = layers.filter((layer) => {
            const {valueKey, title, hide} = layer
            return !(hide || typeof valueKey !== 'string' || typeof title !== 'string')
          })
          
          return l.map((layer: LayerDescription) => {
            const {valueKey, title} = layer
            return {
              key: `${valueKey}`,
              title,
            }
          })
        }
      }
    }
    return []
  }, [dataSources, exportModel])

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1
      if (nextStep === 1 && currentMapCenterPreview) {
        setCurrentMapCenter({
          ...currentMapCenterPreview
        })
      }
      return nextStep
    });
  };

  const enabledDataSources = useMemo(() => {
    // on demand data source are not enabled
    if (dataSources) {
      const keys = Object.keys(dataSources)
      const result: ModelShape = {}
      keys.forEach(key => {
        const item = dataSources[key]
        const {model} = item
        if (model.indexOf('ondemand') === -1) {
          result[key] = item
        }
      })
      return result
    }
    return {}
  }, [dataSources])

  return (
    <Dialog
      id="export-data-modal"
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Export data</ModalTitle>
      <div className={styles.dialogContent}>
        <ErrorMessage message={exportError} />
        <HorizontalStepper
          activeStep={activeStep}
          steps={steps}
        >
          <>
            <p>You can export the data layer as XYZ values in a CSV file. Choose to export either the entire layer or only the data visible within the current map zoom and area.</p>
            <div className={classNames(styles.columns, styles.twoColumns)}>
              <div>
                <ModelSelect
                  dataSources={enabledDataSources}
                  id="purchase-model-select"
                  value={`${exportModel?.key || ''}`}
                  label={'Model'}
                  onChange={(newModelKey: string) => {
                    const [provider, model, geography, version = null] = newModelKey.split('-')
                    const newModel = {
                      key: newModelKey,
                      provider,
                      model,
                      geography,
                      version,
                    } as DataSourceModel
                      setExportModel(newModel)
                    }
                  }
                />
                
                <LayerSelector 
                  id={`layer-selector`}
                  options={layersSelector} 
                  value={selectedLayer}
                  onChange={(layerKey) => {
                    setSelectedLayer(layerKey)
                  }}
                />
                
              </div>
              <div>
                <div className={styles.column}>
                  <h4>{exportModelArea ? `Area model bounds` : `Area current bounds`}</h4>
                  <BoundsBanner enableCopy className={styles.banner} bounds={bounds || null} title={''} />
                  <h4>Current area size</h4>
                  <AreaBanner enableCopy className={styles.banner} bounds={bounds || null} title={''} />
                  {allowExportAllArea && <FormControlLabel
                    sx={{mt: '16px', mb: 0}}
                    control={
                      <Checkbox
                        value="show-model-area" 
                        checked={exportModelArea}
                        onChange={(e: React.SyntheticEvent) => {
                          setExportModelArea( i => !i)
                        }}
                        color="primary" 
                      />}
                    label={`Export all model area`}
                  />}
                </div>
              </div>
              
            </div>
            <div ref={columnRef}>
              {(currentMapCenterPreview && bounds && columnWidth > 0) &&
                  <MapPreview
                    showROI
                    bounds={bounds? [Number(bounds.min.lat), Number(bounds.min.lng), Number(bounds.max.lat),  Number(bounds.max.lng)] : null}
                    onClick={(lat: string, lon: string) => {
                      // if (lat && lon) {
                      //   setLatString(lat)
                      //   setLonString(lon)
                      // }
                    }}
                    // key={`${lon}-${lat}`} 
                    showPoint={false}
                    width={columnWidth}
                    overlayPolygon={`SRID=4326;MULTIPOLYGON(((${bounds.min.lng} ${bounds.min.lat}, ${bounds.max.lng} ${bounds.min.lat}, ${bounds.max.lng} ${bounds.max.lat}, ${bounds.min.lng} ${bounds.max.lat}, ${bounds.min.lng} ${bounds.min.lat})))`}
                    lat={Number(currentMapCenterPreview.location.lat)} 
                    lon={Number(currentMapCenterPreview.location.lng)} 
                  />
              }
            </div>
          </>
          <div>
            <div className={styles.exportPanel}>
              {loading ?
                <Loader center />
              :
                <Button
                  title={'shape-values'}
                  startIcon={<DownloadIcon />}
                  variant='contained'
                  color='success'
                  onClick={() => {
                    let useData: {[key: string]: string | number | null}[] = []
                    if (bounds && exportModel) {
                      let title = `xyz`
                      const {
                        min: {
                          lat: lat_min,
                          lng: lon_min,
                        },
                        max: {
                          lat: lat_max,
                          lng: lon_max,
                        }
                      } = bounds as Bounds
                      const {model, provider, version, geography, key} = exportModel
                      setLoading(true)
                      const execute = async () => {
                        const bbox = (allowExportAllArea && exportModelArea) ? null : [lon_min, lat_min, lon_max, lat_max].map(Number)
                        const params = {
                          lon_min: bbox ? bbox[0] : null,
                          lat_min: bbox ? bbox[1] : null,
                          lon_max: bbox ? bbox[2] : null,
                          lat_max: bbox ? bbox[3] : null,
                          layer: selectedLayer,
                          model, 
                          provider, 
                          version, 
                          geography
                        }
                        const result = await getLayerXYZData(params)

                       trackEvent("exported-layer-data", {uid: account.uid, layer: selectedLayer, model: key })

                        const {error, response, message} = result
                        if (error === 200 && response) {
                          const {result} = response
                          if (result && Array.isArray(result) && result.length > 0) {
                            const str = getStrToCopy(lat_min, lon_min, false) + ', ' + getStrToCopy(lat_max, lon_max, false)
                            title = `${key} ${[lat_min, lon_min, lat_max, lon_max].map(n => numeral(n).format('0.00')).join(', ')}`.replace(/(-null)/g, '')
                            useData = (response.result as {[key: string]: string | number | null}[]).map((item) => {
                              const {value, lon, lat} = item
                              return {
                                lon,
                                lat,
                                [selectedLayer]: value,
                              }
                            })
                            downloadDataAsCSV(useData, `${title} - ${selectedLayer}`, false)
                          } else {
                            setExportError([`No data for **${selectedLayer}**.`])
                          }
                        } else {
                          setExportError([message || `Problem exporting data for **${selectedLayer}**.`])
                        }
                        setLoading(false)
                        // create URL from params
                        const token = await getTempToken()
                        const url = buildUrl(API_BASE, '/api/v1/data/get-layer-xyz', {...params, csv: true, token})
                        setUserMessages(m => {
                          if (useData.length === 0 || error !== 200) {
                            return [
                              ...m,
                              {
                                id: nanoid(),
                                subject: 'Download link cannot be generated',
                                message: `Your download for **${selectedLayer}** cannot be generated. Please try again later or reach out to our team for support.`,
                                status: "new",
                                severity: "error",
                              }
                            ]
                          }
                          return [
                            ...m,
                            {
                              id: nanoid(),
                              subject: 'Download link has been generated',
                              message: `Your download for **${selectedLayer}** is ready. If your download has not started automatically, <a href="${url}" download>please click here to download</a>. The file contains ${numeral(useData.length).format('0,0')} rows of data.`,
                              status: "new",
                              severity: "success",
                            }
                          ]
                        })
                      }
                      execute()
                    }
                    
                  } }
                >
                  Download CSV
                </Button>
              }
            </div>
          </div>
        </HorizontalStepper>
      </div>
      <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
        <>
          {activeStep === steps.length ? 
            <Button onClick={onClose}>Close</Button>
          : 
            <>
              <Button
                color="inherit"
                onClick={activeStep === 0 ? onClose : handleBack}
                sx={{ mr: 1 }}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              {activeStep !== steps.length - 1 && 
                <Button
                  id="cart-modal-next-button"
                  onClick={handleNext}
                >
                  Next
                </Button>
              }
            </>
          }
        </>

      </DialogActions>
    </Dialog>
  );
}
